// src/modules/requerimiento/components/ModemEsperaCard.jsx
// Tarjeta de un módem en espera de plataforma, pensada para leerse de arriba hacia abajo:
//   0. Categoría (adición / mantenimiento / reemplazo / devolución) y qué tiene que hacer Sistemas.
//   1. Veredicto (¿está reportando?) con un color y una frase.
//   2. Quién es: placa, cliente, SIM/EMAI y los datos registrados.
//   3. Verificación en 3 puntos: ¿está en la plataforma indicada?, ¿reporta?, ¿la placa coincide?
//   4. Plataformas donde se encontró, una fila cada una (estado y último reporte); al abrirla, cómo está cargado el equipo ahí.
// En devolución es al revés: solo se verifica que el equipo ya se retiró de la plataforma indicada.
// En un reemplazo se verifican los dos: el nuevo como una adición y el anterior como una devolución.
import React, { useState } from "react";
import {
  Alert, alpha, Box, Button, Chip, CircularProgress, Collapse, Divider, IconButton, Stack, Tooltip, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditIcon from "@mui/icons-material/Edit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HideImageOutlinedIcon from "@mui/icons-material/HideImageOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import SimCardIcon from "@mui/icons-material/SimCard";
import MemoryIcon from "@mui/icons-material/Memory";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import SpeedIcon from "@mui/icons-material/Speed";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import BoltIcon from "@mui/icons-material/Bolt";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import SensorsIcon from "@mui/icons-material/Sensors";
import PublicIcon from "@mui/icons-material/Public";
import RouterIcon from "@mui/icons-material/Router";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import ApartmentIcon from "@mui/icons-material/Apartment";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { haceDias } from "../utils/reporteModem";
import { CLAVE_POR_ID_PLATAFORMA } from "../utils/plataformasModem";
import { categoriaDe, modemAnteriorDe } from "../utils/categoriaPendiente";

const VERDE = "#16A34A";
const AMBAR = "#F59E0B";
const ROJO = "#DC2626";
const GRIS = "#94A3B8";

// Icono de cada plataforma y de cada dato que se muestra (por la etiqueta de la fila).
const ICONO_PLATAFORMA = { flespi1: SensorsIcon, flespi2: SensorsIcon, wialon: PublicIcon, leasecontrol: RouterIcon, rastreoBo: LocalShippingIcon, nacional: LocalGasStationIcon, internacional: LocalGasStationIcon };
const ICONO_FILA = {
  Ident: FingerprintIcon, UID: FingerprintIcon, Subcuenta: AccountTreeOutlinedIcon, "Ignición": PowerSettingsNewIcon, Velocidad: SpeedIcon, "Batería": BatteryFullIcon,
  Voltaje: BoltIcon, GSM: SignalCellularAltIcon, "GPS válido": GpsFixedIcon, Grupo: GroupsIcon, Usuario: PersonOutlineIcon, Placa: DirectionsCarIcon, Alias: LabelOutlinedIcon,
  "Módem": MemoryIcon, Cuadrilla: GroupsIcon, Cliente: BusinessIcon, "Punto cercano": PlaceOutlinedIcon, "Último reporte": ScheduleIcon,
};

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : String(v));
const hay = (v) => v !== null && v !== undefined && String(v).trim() !== "";
const normalizar = (s) => String(s ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

/** Clave de plataforma (flespi1, wialon, rastreoBo…) a la que debía quedar el equipo, según cliente_servicio.web. */
const claveEsperada = (row) => CLAVE_POR_ID_PLATAFORMA[Number(row?.id_plataforma)] || null;

/** En devolución Sistemas da de baja el equipo: se verifica que ya no esté en la plataforma. */
const esDevolucion = (row) => categoriaDe(row).clave === "devolucion";

const simAnterior = (row) => {
  const a = modemAnteriorDe(row);
  return a ? a.num_sim || a.emai : "";
};

/** ¿La placa registrada aparece en lo que la plataforma tiene cargado (nombre o placa)? null si no hay con qué comparar. */
const coincidePlaca = (placa, p) => {
  // En Flespi el nombre del dispositivo es el IMEI, no la placa: no hay con qué comparar.
  if (String(p.key).startsWith("flespi")) return null;
  const buscada = normalizar(placa);
  const cargado = normalizar([p.placa, p.nombre].filter(Boolean).join(" "));
  if (!buscada || !cargado) return null;
  return cargado.includes(buscada);
};

/** "2026-10-16 08:41" -> "16/10 08:41" (más corto para la fila). */
const fechaCorta = (f) => {
  const m = String(f || "").match(/^\d{4}-(\d{2})-(\d{2}) (\d{2}:\d{2})/);
  return m ? `${m[2]}/${m[1]} ${m[3]}` : "";
};

// ── Veredicto, verificación y plataformas ──────────────────────

// tono: "consultando" | "ok" | "aviso" | "error" (los contadores de la página cuentan por tono).
const OK = (titulo, detalle) => ({ tono: "ok", color: VERDE, Icono: CheckCircleIcon, titulo, detalle });
const AVISO = (titulo, detalle) => ({ tono: "aviso", color: AMBAR, Icono: ErrorOutlineIcon, titulo, detalle });
const ERROR = (titulo, detalle) => ({ tono: "error", color: ROJO, Icono: CancelIcon, titulo, detalle });

/**
 * Lo que dice la cabecera: tono, color, icono, frase y detalle. consultaAnterior: plataformas del módem
 * reemplazado (solo en un reemplazo); si el nuevo está bien pero el anterior sigue en la indicada, es error.
 */
export function veredicto(consulta, row, consultaAnterior) {
  const delNuevo = veredictoEquipo(consulta, row);
  if (!modemAnteriorDe(row) || delNuevo.tono === "consultando" || delNuevo.tono === "error") return delNuevo;
  if (!consultaAnterior || consultaAnterior === "verificando") return { tono: "consultando", color: GRIS, Icono: null, titulo: "Consultando el módem anterior…" };
  if (consultaAnterior.estado === "no_se_pudo") return delNuevo;
  const esperada = claveEsperada(row);
  const sigue = esperada && (consultaAnterior.plataformas || []).find((p) => p.key === esperada);
  if (!sigue) return delNuevo;
  return ERROR("Falta quitar el módem anterior", `SIM ${simAnterior(row)} sigue cargado en ${row.plataforma || sigue.titulo}`);
}

function veredictoEquipo(consulta, row) {
  const esperada = claveEsperada(row);
  const nombreEsperada = row?.plataforma;
  if (!consulta || consulta === "verificando") return { tono: "consultando", color: GRIS, Icono: null, titulo: "Consultando plataformas…" };
  if (consulta.estado === "no_se_pudo") return { ...AVISO("No se pudo consultar", "Las plataformas no respondieron. Intenta de nuevo."), Icono: HelpOutlineIcon };
  if (esDevolucion(row)) return veredictoDevolucion(consulta, esperada, nombreEsperada);
  if (consulta.estado === "no_reporta") return ERROR("No está en ninguna plataforma", "No aparece en Flespi, Wialon, LeaseControl, TRS Rastreo ni YPFB.");
  const enEsperada = esperada ? consulta.plataformas.find((p) => p.key === esperada) : null;
  const ref = enEsperada || consulta;
  const cuando = ref.ultimoMs ? `Último reporte ${haceDias(ref.dias)}` : "Sin fecha de último reporte";
  if (esperada && !enEsperada) {
    return AVISO(`No está en ${nombreEsperada || "la plataforma indicada"}`, `Aparece en ${consulta.plataformas.map((p) => p.titulo).join(", ")}.`);
  }
  if (ref.desactualizado) return AVISO("No manda datos recientes", cuando + (enEsperada ? ` en ${enEsperada.titulo}` : ""));
  return OK("Reportando", cuando + (enEsperada ? ` en ${enEsperada.titulo}` : ""));
}

function veredictoDevolucion(consulta, esperada, nombreEsperada) {
  const plataformas = consulta.plataformas || [];
  if (consulta.estado === "no_reporta" || !plataformas.length) return OK("Equipo retirado", "Ya no aparece en ninguna plataforma.");
  if (!esperada) return AVISO("No hay plataforma indicada", `Aparece en ${plataformas.map((p) => p.titulo).join(", ")}.`);
  const enEsperada = plataformas.find((p) => p.key === esperada);
  const nombre = nombreEsperada || enEsperada?.titulo || "la plataforma indicada";
  if (!enEsperada) return OK(`Retirado de ${nombre}`);
  return ERROR(`Sigue cargado en ${nombre}`, enEsperada.ultimoMs ? `Último reporte ${haceDias(enEsperada.dias)}` : "Sin fecha de último reporte");
}

/** Los puntos de la verificación: { ok: true|false|null, texto }. null = no se puede saber. */
function verificaciones(consulta, row, esperada, nombreEsperada, consultaAnterior) {
  const items = verificacionesEquipo(consulta, row, esperada, nombreEsperada);
  if (!items.length || !modemAnteriorDe(row)) return items;
  return [...items, verificacionAnterior(consultaAnterior, row, esperada, nombreEsperada)];
}

function verificacionAnterior(consulta, row, esperada, nombreEsperada) {
  const sim = simAnterior(row);
  if (!consulta || consulta === "verificando") return { ok: null, texto: `Consultando el módem anterior (SIM ${sim})…` };
  if (consulta.estado === "no_se_pudo") return { ok: null, texto: `No se pudo consultar el módem anterior (SIM ${sim})` };
  const plataformas = consulta.plataformas || [];
  if (!plataformas.length) return { ok: true, texto: `El módem anterior (SIM ${sim}) ya no está en ninguna plataforma` };
  if (!esperada) return { ok: null, texto: `El módem anterior (SIM ${sim}) aparece en ${plataformas.map((p) => p.titulo).join(", ")}` };
  return plataformas.some((p) => p.key === esperada)
    ? { ok: false, texto: `El módem anterior (SIM ${sim}) sigue cargado en ${nombreEsperada || "la plataforma indicada"}` }
    : { ok: true, texto: `El módem anterior (SIM ${sim}) ya no está en ${nombreEsperada || "la plataforma indicada"}` };
}

function verificacionesEquipo(consulta, row, esperada, nombreEsperada) {
  if (!consulta || consulta === "verificando" || consulta.estado === "no_se_pudo") return [];
  const plataformas = consulta.plataformas || [];
  const enEsperada = esperada ? plataformas.find((p) => p.key === esperada) : null;
  if (esDevolucion(row)) {
    if (!plataformas.length) return [{ ok: true, texto: "No aparece en ninguna plataforma" }];
    if (!esperada) return [{ ok: null, texto: "No hay plataforma indicada para comparar" }];
    return [enEsperada
      ? { ok: false, texto: `Sigue cargado en ${enEsperada.titulo}` }
      : { ok: true, texto: `Ya no está cargado en ${nombreEsperada || "la plataforma indicada"}` }];
  }
  const items = [];

  items.push(
    esperada
      ? { ok: !!enEsperada, texto: enEsperada ? `Está cargado en ${enEsperada.titulo} (plataforma indicada)` : `No está cargado en ${nombreEsperada || "la plataforma indicada"}` }
      : { ok: null, texto: "No hay plataforma indicada para este cliente" },
  );

  const ref = enEsperada || (plataformas.length ? consulta : null);
  if (!ref) items.push({ ok: false, texto: "No reporta en ninguna plataforma" });
  else if (ref.ultimoMs == null) items.push({ ok: false, texto: "Sin fecha de último reporte" });
  else items.push({ ok: !ref.desactualizado, texto: ref.desactualizado ? `Sin datos ${haceDias(ref.dias)}` : `Reportó ${haceDias(ref.dias)}` });

  const comparables = plataformas.map((p) => coincidePlaca(row.placa, p)).filter((v) => v !== null);
  if (!hay(row.placa)) items.push({ ok: null, texto: "No hay placa registrada para comparar" });
  else if (!comparables.length) items.push({ ok: null, texto: "La placa no se puede comparar (Flespi guarda el IMEI)" });
  else if (comparables.some(Boolean)) items.push({ ok: true, texto: `La placa ${row.placa} coincide con la plataforma` });
  else items.push({ ok: false, texto: `La placa ${row.placa} no coincide con lo cargado en la plataforma` });
  return items;
}

const Verificacion = ({ ok, texto }) => {
  const Icono = ok === true ? CheckCircleIcon : ok === false ? CancelIcon : HelpOutlineIcon;
  const color = ok === true ? VERDE : ok === false ? ROJO : GRIS;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Icono sx={{ color, fontSize: 18 }} />
      <Typography sx={{ fontSize: 13, fontWeight: ok === false ? 700 : 500, color: ok === false ? ROJO : "text.primary" }}>{texto}</Typography>
    </Stack>
  );
};

/** Una plataforma: fila con estado y último reporte; al abrirla, cómo está cargado el equipo ahí. */
function FilaPlataforma({ p, indicada, placaRegistrada, abiertaInicial }) {
  const [abierta, setAbierta] = useState(abiertaInicial);
  const color = p.dias == null || p.desactualizado ? AMBAR : VERDE;
  const IconoPlat = ICONO_PLATAFORMA[p.key] || SensorsIcon;
  const placaOk = coincidePlaca(placaRegistrada, p);
  const filas = p.filas.filter((f) => f.label !== "Último reporte");
  const ultimo = p.filas.find((f) => f.label === "Último reporte")?.value;

  return (
    <Box sx={{ border: "1px solid", borderColor: indicada ? alpha(p.color, 0.6) : "divider", borderRadius: 1.5, overflow: "hidden" }}>
      <Stack
        direction="row" alignItems="center" spacing={1} onClick={() => setAbierta((v) => !v)}
        sx={{ px: 1, py: 0.75, cursor: "pointer", bgcolor: indicada ? alpha(p.color, 0.07) : "transparent", "&:hover": { bgcolor: alpha(p.color, 0.1) } }}
      >
        <Box sx={{ width: 28, height: 28, borderRadius: "50%", display: "grid", placeItems: "center", color: p.color, bgcolor: alpha(p.color, 0.15), flexShrink: 0 }}>
          <IconoPlat sx={{ fontSize: 17 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 800 }}>{p.titulo}</Typography>
            {indicada && (
              <Tooltip title="Plataforma donde debía quedar el equipo">
                <StarIcon sx={{ fontSize: 14, color: AMBAR }} />
              </Tooltip>
            )}
          </Stack>
          <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>{p.nombre || "Sin nombre"}</Typography>
        </Box>
        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          <Stack direction="row" alignItems="center" spacing={0.4} justifyContent="flex-end">
            {color === VERDE ? <CheckCircleIcon sx={{ color, fontSize: 15 }} /> : <ErrorOutlineIcon sx={{ color, fontSize: 15 }} />}
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color }}>{p.dias == null ? "Sin fecha" : haceDias(p.dias)}</Typography>
          </Stack>
          {ultimo && <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{fechaCorta(ultimo)}</Typography>}
        </Box>
        <ExpandMoreIcon sx={{ color: "text.secondary", transition: "transform 150ms", transform: abierta ? "rotate(180deg)" : "none" }} />
      </Stack>
      <Collapse in={abierta} unmountOnExit>
        <Divider />
        <Box sx={{ px: 1.25, py: 1, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", columnGap: 2, rowGap: 0.75 }}>
          {placaRegistrada && placaOk !== null && (
            <Box sx={{ gridColumn: "1 / -1" }}>
              <Chip size="small" variant="outlined" color={placaOk ? "success" : "warning"} label={placaOk ? "Placa coincide con la registrada" : "Placa distinta a la registrada"} sx={{ height: 22, fontSize: 11 }} />
            </Box>
          )}
          {filas.map((f) => {
            const Icono = ICONO_FILA[f.label];
            return (
              <Box key={f.label} sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={0.4}>
                  {Icono && <Icono sx={{ fontSize: 12, color: "text.disabled" }} />}
                  <Typography sx={{ fontSize: 10.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 }}>{f.label}</Typography>
                </Stack>
                <Typography noWrap title={String(f.value)} sx={{ fontSize: 12.5, fontWeight: 700 }}>{f.value}</Typography>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

const Titulo = ({ children, extra }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.6 }}>{children}</Typography>
    {extra}
  </Stack>
);

/** Primera fila: píldora de la categoría, el requerimiento y qué tiene que hacer Sistemas (en un reemplazo, a qué módem reemplazó). */
function CategoriaFila({ row, onVerReq, onVerEquipoAnterior }) {
  const cat = categoriaDe(row);
  const { Icono } = cat;
  const anterior = simAnterior(row);
  return (
    <Box sx={{ px: 1.75, py: 1, bgcolor: alpha(cat.color, 0.08), display: "grid", gap: 0.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ px: 1, py: 0.25, borderRadius: 99, bgcolor: cat.color, color: "#fff" }}>
          <Icono sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 0.5, textTransform: "uppercase" }}>{cat.etiqueta}</Typography>
        </Stack>
        {/* El tipo del requerimiento solo si dice algo distinto de la píldora (reemplazo -> "Mantenimiento"). */}
        {(cat.clave === "reemplazo" || cat.clave === "otro") && row.tipo_req && (
          <Typography noWrap sx={{ fontSize: 11.5, color: "text.secondary" }}>{row.tipo_req}</Typography>
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onVerReq} sx={{ minWidth: 0, px: 0.5, fontWeight: 800 }}>Req #{row.cod}</Button>
      </Stack>
      <Stack direction="row" alignItems="flex-start" spacing={0.75}>
        <TaskAltIcon sx={{ fontSize: 16, color: cat.color, mt: 0.15 }} />
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: cat.color }}>{cat.queHacer}</Typography>
      </Stack>
      {anterior && (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <SwapHorizIcon sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
            Reemplaza al SIM{" "}
            <Box component="span" role="button" onClick={onVerEquipoAnterior} sx={{ fontWeight: 700, color: "primary.main", textDecoration: "underline", cursor: "pointer" }}>{anterior}</Box>
          </Typography>
        </Stack>
      )}
    </Box>
  );
}

export default function ModemEsperaCard({
  row, consulta, consultaAnterior, onConsultar, onVerReq, onVerEquipo, onVerEquipoAnterior, onVerImagen, onRevisar, formatDateTime, toAbsUrl,
}) {
  const esperada = claveEsperada(row);
  const v = veredicto(consulta, row, consultaAnterior);
  const verificando = !consulta || consulta === "verificando";
  const plataformas = !verificando && consulta.plataformas ? consulta.plataformas : [];
  // La indicada primero, luego las demás por fecha de reporte (la más reciente arriba).
  const ordenadas = [...plataformas].sort((a, b) => (b.key === esperada) - (a.key === esperada) || (b.ultimoMs || 0) - (a.ultimoMs || 0));
  const checks = verificaciones(consulta, row, esperada, row.plataforma, consultaAnterior);
  const masDatos = [
    hay(row.id_ref) && { Icono: LabelOutlinedIcon, etiqueta: "Alias", valor: row.id_ref },
    hay(row.grupo) && { Icono: GroupsIcon, etiqueta: "Grupo", valor: row.grupo },
    hay(row.sociedad) && { Icono: ApartmentIcon, etiqueta: "Sociedad", valor: row.sociedad },
    hay(row.transportista) && { Icono: LocalShippingIcon, etiqueta: "Transportista", valor: row.transportista },
  ].filter(Boolean);
  const IconoVeredicto = v.Icono;

  return (
    <Box sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: "background.paper", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      {/* 0. Categoría y qué hacer */}
      <CategoriaFila row={row} onVerReq={onVerReq} onVerEquipoAnterior={onVerEquipoAnterior} />

      {/* 1. Veredicto */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 1.75, py: 1.25, bgcolor: alpha(v.color, 0.12), borderBottom: `3px solid ${v.color}` }}>
        {/* Por el veredicto y no por `verificando`: en un reemplazo puede seguir consultando el módem anterior. */}
        {v.tono === "consultando" || !IconoVeredicto ? <CircularProgress size={26} sx={{ color: v.color }} /> : <IconoVeredicto sx={{ color: v.color, fontSize: 30 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.2 }}>{v.titulo}</Typography>
          {v.detalle && <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{v.detalle}</Typography>}
        </Box>
        <Tooltip title="Consultar de nuevo las plataformas">
          <span>
            <IconButton size="small" aria-label="Consultar de nuevo" onClick={onConsultar} disabled={verificando}><RefreshIcon fontSize="small" /></IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ p: 1.75, display: "grid", gap: 1.75 }}>
        {/* 2. Quién es */}
        <Box>
          <Titulo>Equipo registrado</Titulo>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0, display: "grid", gap: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, py: 0.25, border: "2px solid", borderColor: "text.primary", borderRadius: 1, alignSelf: "flex-start", justifySelf: "flex-start" }}>
                <DirectionsCarIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: 17, fontWeight: 900, letterSpacing: 1 }}>{hay(row.placa) ? row.placa : "SIN PLACA"}</Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <BusinessIcon sx={{ fontSize: 17, color: "primary.main" }} />
                <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{dash(row.razon_social)}</Typography>
              </Stack>
              <Typography sx={{ fontSize: 12, color: "text.secondary", pl: 3 }}>{row.id_serv} - {dash(row.descripcion)}</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ pt: 0.25 }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <SimCardIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                  <Typography role="button" onClick={onVerEquipo} sx={{ fontSize: 12.5, fontWeight: 700, color: "primary.main", textDecoration: "underline", cursor: "pointer" }}>{dash(row.num_sim)}</Typography>
                </Stack>
                {hay(row.emai) && row.emai !== row.num_sim && (
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <MemoryIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                    <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>{row.emai}</Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
            {row.imagen_url ? (
              <Tooltip title="Ver imagen de la instalación">
                <Box
                  component="img" src={toAbsUrl(row.imagen_url)} alt="Evidencia" onClick={onVerImagen}
                  sx={{ width: 64, height: 64, objectFit: "cover", borderRadius: 1.5, border: "1px solid", borderColor: "divider", cursor: "pointer", flexShrink: 0 }}
                />
              </Tooltip>
            ) : (
              <Tooltip title="Sin imagen de la instalación"><HideImageOutlinedIcon sx={{ fontSize: 28, color: "text.disabled" }} /></Tooltip>
            )}
          </Stack>
          {masDatos.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              {masDatos.map(({ Icono, etiqueta, valor }) => (
                <Chip key={etiqueta} size="small" variant="outlined" icon={<Icono sx={{ fontSize: "14px !important" }} />} label={`${etiqueta}: ${valor}`} sx={{ height: 22, fontSize: 11.5 }} />
              ))}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* 3. Verificación */}
        <Box>
          <Titulo extra={<Chip size="small" icon={<StarIcon sx={{ fontSize: "13px !important" }} />} label={`Indicada: ${dash(row.plataforma)}`} sx={{ height: 20, fontSize: 11 }} />}>Verificación</Titulo>
          {verificando ? (
            <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Consultando las plataformas…</Typography>
          ) : consulta.estado === "no_se_pudo" ? (
            <Alert severity="warning" sx={{ py: 0 }} action={<Button size="small" onClick={onConsultar}>Reintentar</Button>}>No se pudieron consultar las plataformas.</Alert>
          ) : (
            <Stack spacing={0.6}>{checks.map((c, i) => <Verificacion key={i} {...c} />)}</Stack>
          )}
        </Box>

        {/* 4. Plataformas */}
        {ordenadas.length > 0 && (
          <Box>
            <Titulo extra={<Typography sx={{ fontSize: 12, color: "text.secondary" }}>Toca una para ver cómo está cargado</Typography>}>
              {`Plataformas (${ordenadas.length})`}
            </Titulo>
            <Stack spacing={0.75}>
              {ordenadas.map((p) => <FilaPlataforma key={p.key} p={p} indicada={p.key === esperada} placaRegistrada={row.placa} abiertaInicial={p.key === esperada} />)}
            </Stack>
          </Box>
        )}

        {(row.observacion || row.obs_req) && (
          <Stack direction="row" spacing={0.75} alignItems="flex-start">
            <StickyNote2OutlinedIcon sx={{ fontSize: 16, color: "text.secondary", mt: 0.2 }} />
            <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>{row.observacion || row.obs_req}</Typography>
          </Stack>
        )}
      </Box>

      {/* Acciones */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" sx={{ mt: "auto", px: 1.75, py: 1, borderTop: "1px solid", borderColor: "divider", bgcolor: (t) => alpha(t.palette.text.primary, 0.03) }}>
        <Typography sx={{ fontSize: 11.5, color: "text.secondary", flex: 1 }}>Proceso: {formatDateTime(row.fecha_proceso)}</Typography>
        {row.url_plataforma && (
          <Button
            size="small" startIcon={<OpenInNewIcon fontSize="small" />} sx={{ textTransform: "none" }}
            onClick={() => window.open(/^https?:\/\//i.test(row.url_plataforma) ? row.url_plataforma : `https://${row.url_plataforma}`, "_blank", "noopener,noreferrer")}
          >
            Abrir plataforma
          </Button>
        )}
        <Button size="small" variant="contained" startIcon={<EditIcon fontSize="small" />} onClick={onRevisar} sx={{ textTransform: "none", fontWeight: 700 }}>
          Revisar
        </Button>
      </Stack>
    </Box>
  );
}
